// ProjectDrawer — slide-in side panel opened by clicking a bar on the
// roadmap. Lets users get a quick read on a project (description, team,
// health, workstream progress) without leaving the roadmap context.
// Includes an "Open project" button for full detail navigation.

import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';

import {
  DEFAULT_WORKSTREAM_COLOR,
  PROJECT_PRIORITY_COLORS,
  PROJECT_PRIORITY_LABELS,
} from '../../pm/constants';
import { useProjectDepartments } from '../../pm/useProjectDepartments';
import { formatDateRange } from '../../pm/dueDate';
import { computeHealth } from '../../pm/health';
import { AppUser, Project, Task, Workstream } from '../../pm/types';
import AvatarStack from './AvatarStack';
import HealthPill from './HealthPill';
import { IconClose } from './Icons';

interface Props {
  projectId: string | null;
  projects: Project[];
  tasks: Task[];
  workstreams: Workstream[];
  admins: AppUser[];
  onClose: () => void;
}

const ProjectDrawer: React.FC<Props> = ({
  projectId,
  projects,
  tasks,
  workstreams,
  admins,
  onClose,
}) => {
  const history = useHistory();
  const open = !!projectId;
  // Cache the last project so the slide-out keeps rendering during the
  // close animation instead of flashing empty.
  const lastRef = useRef<Project | null>(null);
  const current = projectId ? projects.find((p) => p.id === projectId) : null;
  if (current) lastRef.current = current;
  const shown = current || lastRef.current;

  // Escape closes when the drawer is visible.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const projectTasks = useMemo(() => {
    if (!shown) return [];
    return tasks.filter((t) => t.projectId === shown.id);
  }, [tasks, shown]);

  const projectWorkstreams = useMemo(() => {
    if (!shown) return [];
    return workstreams
      .filter((w) => w.projectId === shown.id && !w.archived)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [workstreams, shown]);

  const memberEmails = useMemo(() => {
    if (!shown) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of projectTasks) {
      if (!t.ownerId || seen.has(t.ownerId)) continue;
      seen.add(t.ownerId);
      const u = admins.find((a) => a.uid === t.ownerId);
      if (u?.email) out.push(u.email);
    }
    return out;
  }, [projectTasks, admins, shown]);

  // Hooks must always run in the same order — call the departments hook
  // BEFORE the `if (!shown)` early-return below. Originally it lived
  // alongside the resolved-project derivations and tripped
  // react-hooks/rules-of-hooks once the early return existed.
  const { getDept } = useProjectDepartments();

  if (!shown) {
    // Wrap in .pm-app so the --rr-*/--pm-* CSS variables (scoped to .pm-app)
    // resolve correctly when this portal mounts outside the PM Suite tree.
    return createPortal(
      <div className="pm-app pm-dr-portal">
        <div className={`pm-dr-scrim${open ? ' is-open' : ''}`} onClick={onClose} />
        <aside className={`pm-dr${open ? ' is-open' : ''}`} aria-hidden={!open} />
      </div>,
      document.body,
    );
  }

  const total = projectTasks.length;
  const done = projectTasks.filter((t) => t.status === 'done').length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const health = computeHealth(shown, projectTasks);
  // getDept comes from the hook called above the early return — reusing
  // the same instance keeps the hook call order stable.
  const dept = getDept(shown.department);
  const priLabel = shown.priority ? PROJECT_PRIORITY_LABELS[shown.priority] : 'Unprioritized';
  const priColor = shown.priority
    ? PROJECT_PRIORITY_COLORS[shown.priority]
    : 'var(--rr-stone-400)';
  const scheduled =
    typeof shown.startDate === 'number' && typeof shown.targetDate === 'number';

  // Portal to body so the drawer + scrim escape IonRouterOutlet's stacking
  // context and float above the notification bell. Wrap in .pm-app to
  // reinstate the CSS-variable scope that lives on that class — otherwise
  // tokens like --rr-paper / --pm-border resolve to nothing inside the
  // portaled subtree, which is what made the rail look transparent.
  return createPortal(
    <div className="pm-app pm-dr-portal">
      <div className={`pm-dr-scrim${open ? ' is-open' : ''}`} onClick={onClose} />
      <aside
        className={`pm-dr${open ? ' is-open' : ''}`}
        role="dialog"
        aria-label="Project preview"
        aria-hidden={!open}
      >
        <div className="pm-dr-hd">
          <div className="pm-dr-hd-titles">
            <div className="pm-dr-eyebrow">
              <span className="pm-pri-dot" style={{ background: priColor }} />
              {priLabel} priority
            </div>
            <h2 className="pm-dr-title">{shown.title}</h2>
            <p className="pm-dr-sub">
              {scheduled
                ? formatDateRange(shown.startDate as number, shown.targetDate as number)
                : 'Unscheduled'}
            </p>
          </div>
          <button
            type="button"
            className="pm-dr-close"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <IconClose size={16} />
          </button>
        </div>

        <div className="pm-dr-body">
          {shown.description ? (
            <section className="pm-dr-section">
              <p className="pm-dr-desc">{shown.description}</p>
            </section>
          ) : null}

          <section className="pm-dr-section">
            <h4>At a glance</h4>
            <dl className="pm-dr-kv">
              <dt>Team</dt>
              <dd>
                {memberEmails.length === 0 ? (
                  <span className="pm-dr-muted">No assignments yet</span>
                ) : (
                  <AvatarStack emails={memberEmails} max={5} size={20} />
                )}
              </dd>
              <dt>Department</dt>
              <dd>
                {dept ? (
                  <>
                    <span className="pm-pri-dot" style={{ background: dept.swatch, width: 6, height: 6 }} />
                    {dept.label}
                  </>
                ) : (
                  <span className="pm-dr-muted">Unassigned</span>
                )}
              </dd>
              <dt>Health</dt>
              <dd>
                <HealthPill health={health} />
              </dd>
              <dt>Progress</dt>
              <dd>
                {done}/{total} tasks · {pct}%
              </dd>
            </dl>
          </section>

          <section className="pm-dr-section">
            <h4>Workstreams ({projectWorkstreams.length})</h4>
            <div className="pm-dr-ws-list">
              {projectWorkstreams.length === 0 ? (
                <div className="pm-dr-muted">No workstreams yet.</div>
              ) : (
                projectWorkstreams.map((w) => {
                  const wsTasks = projectTasks.filter((t) => t.workstreamId === w.id);
                  const wDone = wsTasks.filter((t) => t.status === 'done').length;
                  const wPct = wsTasks.length > 0 ? Math.round((wDone / wsTasks.length) * 100) : 0;
                  const color = w.color || DEFAULT_WORKSTREAM_COLOR;
                  return (
                    <div key={w.id} className="pm-dr-ws-row">
                      <span
                        className="pm-dr-ws-dot"
                        style={{ background: color }}
                        aria-hidden="true"
                      />
                      <div className="pm-dr-ws-main">
                        <div className="pm-dr-ws-name">{w.title}</div>
                        <div className="pm-dr-ws-bar">
                          <i style={{ width: `${wPct}%`, background: color }} />
                        </div>
                      </div>
                      <div className="pm-dr-ws-ct">
                        {wDone}/{wsTasks.length}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          <div className="pm-dr-actions">
            <button
              type="button"
              className="pm-button pm-button-primary"
              onClick={() => {
                history.push(`/Projects/${shown.id}`);
                onClose();
              }}
            >
              Open project
            </button>
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default ProjectDrawer;
